import { useState, useEffect, useRef, useCallback } from 'react';
import {
    FaSearch, FaFilter, FaFilePdf, FaChevronLeft, FaChevronRight, FaTimes,
    FaExclamationTriangle, FaTrashAlt, FaCalendarAlt, FaFileAlt, FaClock,
    FaHashtag, FaSignature, FaRulerCombined, FaTree, FaUserTie, FaEllipsisV
} from 'react-icons/fa';
import {useNavigate } from 'react-router-dom';
import logo from '../../../../img/logo rio verde pdf.png';
import styles from './ViewImoveis.module.css';
import { API_BASE_URL } from "../../../../config";
import { gerarRelatorioDeImoveis, gerarRelatorioDePrevisao, gerarRelatorioDeVencimentos } from './reportGeneratorViewImoveis'
import PageHeader from '../../../../components/PageHeader/PageHeader';
import LoadingOverlay from '../../../../components/LoadingOverlay/LoadingOverlay';

const ViewImoveis = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const chartContainerRef = useRef(null);
    const [imoveis, setImoveis] = useState([]);
    const [sortCriteria, setSortCriteria] = useState('');
    const [tipoImovelFiltro, setTipoImovelFiltro] = useState('todos');
    const [filters, setFilters] = useState({
        especie: '',
        proprietario: '',
        area_imovel_min: '',
        area_imovel_max: '',
        data_contrato_start: '',
        data_contrato_end: '',
    });
    const [reportFilters, setReportFilters] = useState({
        tipoImovel: 'todos',
        atributos: {
            descricao: false, area_imovel: false, area_plantio: false, especie: false,
            num_arvores_plantadas: false, num_arvores_cortadas: false, num_arvores_remanescentes: false,
            matricula: false, data_plantio: false, vencimento_contrato: false, data_contrato: false,
            proprietario: false, arrendatario: false, municipio: false, localidade: false,
            altura_desrama: false, numero_car: false, codigo_cc: false,
        },
    });
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 10;
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [itemToDeleteId, setItemToDeleteId] = useState(null);
    const [showDesramaModal, setShowDesramaModal] = useState(false);
    const [showDesbasteModal, setShowDesbasteModal] = useState(false);
    const [showVencimentoModal, setShowVencimentoModal] = useState(false);
    const [anoPrevisao, setAnoPrevisao] = useState(new Date().getFullYear());
    const [showReportDropdown, setShowReportDropdown] = useState(false);
    const reportDropdownRef = useRef(null);
    const [pageLoading, setPageLoading] = useState(true);
    const navigate = useNavigate();

    // CORREÇÃO: Envolvendo fetchImoveis em useCallback para estabilizar sua referência
    const fetchImoveis = useCallback(async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            console.error("Usuário não autenticado. Redirecionando para login.");
            navigate('/'); 
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/imoveis`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.status === 401 || response.status === 400) {
                 console.error("Token inválido ou expirado. Redirecionando para login.");
                 localStorage.removeItem('token');
                 navigate('/');
                 return;
            }

            const data = await response.json();
            if (response.ok && Array.isArray(data)) {
                setImoveis(data);
            } else {
                console.error('Falha ao buscar imóveis ou resposta inválida:', data);
                setImoveis([]);
            }
        } catch (error) {
            console.error('Erro de conexão ao buscar imóveis:', error);
            setImoveis([]);
        }
    }, [navigate]); // Adicionada dependência 'navigate'

    useEffect(() => {
        const MIN_LOADING_TIME = 300;
        const startTime = Date.now();

        const loadData = async () => {
            await fetchImoveis();
            
            const endTime = Date.now();
            const duration = endTime - startTime;
            const remainingTime = MIN_LOADING_TIME - duration;

            if (remainingTime > 0) {
                setTimeout(() => setPageLoading(false), remainingTime);
            } else {
                setPageLoading(false);
            }
        };
        
        loadData();
    }, [fetchImoveis]); // CORREÇÃO: Adicionada fetchImoveis como dependência

    const handleDelete = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            console.error("Usuário não autenticado. Ação de exclusão bloqueada.");
            navigate('/');
            return;
        }

        if (itemToDeleteId) {
            try {
                const response = await fetch(`${API_BASE_URL}/api/imoveis/${itemToDeleteId}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (response.ok) {
                    setImoveis(imoveis.filter((imovel) => imovel.id !== itemToDeleteId));
                } else {
                    const errorData = await response.json();
                    console.error('Falha ao excluir o imóvel no backend:', errorData.error);
                }
            } catch (error) {
                console.error('Erro de conexão ao excluir imóvel:', error);
            } finally {
                setShowDeleteConfirmation(false);
                setItemToDeleteId(null);
            }
        }
    };

    const handleGerarRelatorioPrevisao = (tipo) => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/');
            return;
        }
        gerarRelatorioDePrevisao({
            tipo,
            anoPrevisao,
            imoveis,
            logo,
            apiBaseUrl: API_BASE_URL,
            token 
        });
        setShowDesramaModal(false);
        setShowDesbasteModal(false);
    };

    const handleGerarRelatorioVencimentos = () => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/');
            return;
        }
        gerarRelatorioDeVencimentos({
            anoVencimento: anoPrevisao,
            imoveis,
            logo,
            token 
        });
        setShowVencimentoModal(false);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (reportDropdownRef.current && !reportDropdownRef.current.contains(event.target)) {
                setShowReportDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleImoveisReportClick = () => {
        setShowReportModal(true);
        setShowReportDropdown(false);
    };

    const handleDesramaReportClick = () => {
        setShowDesramaModal(true);
        setShowReportDropdown(false);
    };

    const handleDesbasteReportClick = () => {
        setShowDesbasteModal(true);
        setShowReportDropdown(false);
    };

    const handleVencimentoReportClick = () => {
        setShowVencimentoModal(true);
        setShowReportDropdown(false);
    };

    const friendlyNames = {
        descricao: 'Descrição', area_imovel: 'Área do Imóvel (ha)',
        area_plantio: 'Área de Plantio (ha)', especie: 'Espécie',
        num_arvores_plantadas: 'Nº de Árvores Plantadas', num_arvores_cortadas: 'Nº de Árvores Cortadas',
        num_arvores_remanescentes: 'Nº de Árvores Remanescentes', matricula: 'Matrícula',
        data_plantio: 'Data de Plantio', vencimento_contrato: 'Vencimento do Contrato',
        data_contrato: 'Data do Contrato', proprietario: 'Proprietário',
        arrendatario: 'Arrendatário', municipio: 'Município',
        localidade: 'Localidade', altura_desrama: 'Altura da Desrama',
        numero_car: 'Número do CAR', codigo_cc: 'Código CC',
    };

    const getFriendlyName = (key) => friendlyNames[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filters, sortCriteria, tipoImovelFiltro]);

    const handleSearchChange = (e) => setSearchTerm(e.target.value);

    const confirmDelete = (id) => {
        setItemToDeleteId(id);
        setShowDeleteConfirmation(true);
    };

    const cancelDelete = () => {
        setShowDeleteConfirmation(false);
        setItemToDeleteId(null);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleReportFilterChange = (e) => {
        const { name, value } = e.target;
        setReportFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleReportAtributosChange = (e) => {
        const { name, checked } = e.target;
        setReportFilters((prev) => ({
            ...prev,
            atributos: { ...prev.atributos, [name]: checked },
        }));
    };

    const formatarData = (data) => {
        if (!data) return "N/A";
        const date = new Date(data);
        if (isNaN(date.getTime())) return "N/A";
        const dia = String(date.getUTCDate()).padStart(2, '0');
        const mes = String(date.getUTCMonth() + 1).padStart(2, '0');
        const ano = date.getUTCFullYear();
        return `${dia}/${mes}/${ano}`;
    };

    const formatarNumero = (num) => {
        if (num === null || num === undefined || String(num).trim() === '') return "N/A";
        const numero = Number(String(num).replace(',', '.'));
        if (isNaN(numero)) return "N/A";
        return numero.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const filteredImoveis = imoveis
        .filter((imovel) => {
            const matchesSearchTerm = imovel.descricao?.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesEspecie = imovel.especie?.toLowerCase().includes(filters.especie.toLowerCase());
            const matchesProprietario = imovel.proprietario?.toLowerCase().includes(filters.proprietario.toLowerCase());
            const areaMinText = String(filters.area_imovel_min || '').replace(',', '.');
            const areaMaxText = String(filters.area_imovel_max || '').replace(',', '.');
            const matchesAreaImovel =
                (areaMinText ? imovel.area_imovel >= parseFloat(areaMinText) : true) &&
                (areaMaxText ? imovel.area_imovel <= parseFloat(areaMaxText) : true);
            const matchesDataContrato =
                (filters.data_contrato_start ? new Date(imovel.data_contrato) >= new Date(filters.data_contrato_start) : true) &&
                (filters.data_contrato_end ? new Date(imovel.data_contrato) <= new Date(filters.data_contrato_end) : true);

            const matchesTipoImovelPagina =
                (tipoImovelFiltro === 'todos') ||
                (tipoImovelFiltro === 'proprio' && (!imovel.arrendatario || imovel.arrendatario.trim() === '')) ||
                (tipoImovelFiltro === 'arrendado' && imovel.arrendatario && imovel.arrendatario.trim() !== '');

            return matchesSearchTerm && matchesEspecie && matchesProprietario && matchesAreaImovel && matchesDataContrato && matchesTipoImovelPagina;
        })
        .sort((a, b) => {
            if (!sortCriteria) return 0;
            const valA = a[sortCriteria] || '';
            const valB = b[sortCriteria] || '';
            switch (sortCriteria) {
                case 'descricao': case 'proprietario':
                    return valA.localeCompare(valB);
                case 'area_plantio':
                    return (b.area_plantio || 0) - (a.area_plantio || 0);
                case 'data_plantio_desc':
                    return new Date(b.data_plantio) - new Date(a.data_plantio);
                case 'codigo_cc':
                    return String(valA).localeCompare(String(valB), undefined, { numeric: true });
                default:
                    return 0;
            }
        });

    const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
    const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
    const currentItems = filteredImoveis.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredImoveis.length / ITEMS_PER_PAGE);

    const paginate = (pageNumber) => {
        if (pageNumber < 1 || pageNumber > totalPages) return;
        setCurrentPage(pageNumber);
    };

    const selecionarTodosAtributos = (selecionar) => {
        const todosAtributos = Object.keys(reportFilters.atributos).reduce((acc, key) => {
            acc[key] = selecionar;
            return acc;
        }, {});
        setReportFilters((prev) => ({ ...prev, atributos: todosAtributos }));
    };

    const handleGerarRelatorioImoveis = () => {
        const imoveisParaRelatorio = filteredImoveis.filter(imovel => {
            return (reportFilters.tipoImovel === 'todos' ||
                    (reportFilters.tipoImovel === 'proprio' && (!imovel.arrendatario || imovel.arrendatario.trim() === '')) ||
                    (reportFilters.tipoImovel === 'arrendado' && imovel.arrendatario && imovel.arrendatario.trim() !== ''));
        });

        gerarRelatorioDeImoveis({
            filteredImoveis: imoveisParaRelatorio,
            reportFilters,
            getFriendlyName,
            chartContainerRef,
            logo
        });
        setShowReportModal(false);
    };

    const handleRowClick = (imovelId) => {
        navigate(`/imovel/${imovelId}`);
    };

    return (
        <div className={styles.viewImoveisContainer}>
            {pageLoading && <LoadingOverlay text="Carregando imóveis..." />}

            {!pageLoading && (
                <>
                    <div ref={chartContainerRef} style={{ position: 'absolute', top: '-9999px', left: '-9999px' }}></div>
                    
                    <PageHeader 
                        title="Gerenciamento de Imóveis" 
                        subtitle="Visualize, filtre e gere relatórios de todos os imóveis cadastrados."
                        backTo="/sistema-imoveis"
                    />

                    {showDeleteConfirmation && (
                        <div className={styles.modalOverlay} onClick={cancelDelete}>
                            <div className={`${styles.modalContent} ${styles.deleteConfirmationModal}`} onClick={(e) => e.stopPropagation()}>
                                <FaExclamationTriangle className={styles.warningIcon} />
                                <h3>Confirmar Exclusão</h3>
                                <p>Tem certeza que deseja excluir este imóvel? Esta ação não pode ser desfeita.</p>
                                <div className={styles.modalFooterStacked}>
                                    <button className={`${styles.btn} ${styles.btnDanger}`} onClick={handleDelete}>
                                        <FaTrashAlt /> Confirmar Exclusão
                                    </button>
                                    <button className={styles.btnLink} onClick={cancelDelete}>
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {showFilterModal && (
                        <div className={styles.modalOverlay} onClick={() => setShowFilterModal(false)}>
                            <div className={`${styles.modalContent} ${styles.filterModal}`} onClick={(e) => e.stopPropagation()}>
                                <div className={styles.modalHeader}>
                                    <h3>Filtrar Imóveis</h3>
                                    <button className={styles.modalCloseBtn} onClick={() => setShowFilterModal(false)}><FaTimes /></button>
                                </div>
                                <div className={styles.modalBody}>
                                    <div className={styles.filterFormGrid}>
                                        <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                                            <label htmlFor="especie">Espécie:</label>
                                            <input type="text" id="especie" name="especie" value={filters.especie} onChange={handleFilterChange} />
                                        </div>
                                        <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                                            <label htmlFor="proprietario">Proprietário:</label>
                                            <input type="text" id="proprietario" name="proprietario" value={filters.proprietario} onChange={handleFilterChange} />
                                        </div>
                                        <div className={styles.formGroup}>
                                            <label htmlFor="area_imovel_min">Área mínima (ha):</label>
                                            <input type="number" step="any" id="area_imovel_min" name="area_imovel_min" value={filters.area_imovel_min} onChange={handleFilterChange} />
                                        </div>
                                        <div className={styles.formGroup}>
                                            <label htmlFor="area_imovel_max">Área máxima (ha):</label>
                                            <input type="number" step="any" id="area_imovel_max" name="area_imovel_max" value={filters.area_imovel_max} onChange={handleFilterChange} />
                                        </div>
                                        <div className={styles.formGroup}>
                                            <label htmlFor="data_contrato_start">Data contrato (início):</label>
                                            <input type="date" id="data_contrato_start" name="data_contrato_start" value={filters.data_contrato_start} onChange={handleFilterChange} />
                                        </div>
                                        <div className={styles.formGroup}>
                                            <label htmlFor="data_contrato_end">Data contrato (fim):</label>
                                            <input type="date" id="data_contrato_end" name="data_contrato_end" value={filters.data_contrato_end} onChange={handleFilterChange} />
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.modalFooter}>
                                    <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => setShowFilterModal(false)}>Aplicar Filtros</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {showReportModal && (
                        <div className={styles.modalOverlay} onClick={() => setShowReportModal(false)}>
                            <div className={`${styles.modalContent} ${styles.modalLg} ${styles.reportModal}`} onClick={(e) => e.stopPropagation()}>
                                <div className={styles.modalHeader}>
                                    <h3>Gerar Relatório de Imóveis</h3>
                                    <button className={styles.modalCloseBtn} onClick={() => setShowReportModal(false)}><FaTimes /></button>
                                </div>
                                <div className={styles.modalBody}>
                                    <div className={styles.formGroup}>
                                        <label htmlFor="tipoImovel">Filtrar por Tipo de Imóvel:</label>
                                        <select id="tipoImovel" name="tipoImovel" value={reportFilters.tipoImovel} onChange={handleReportFilterChange}>
                                            <option value="todos">Todos</option>
                                            <option value="proprio">Próprio</option>
                                            <option value="arrendado">Arrendado</option>
                                        </select>
                                    </div>
                                    <hr />
                                    <h6>Selecione os Atributos para o Relatório:</h6>
                                    <div className={styles.reportAttributesActions}>
                                        <button type="button" className={`${styles.btn} ${styles.btnOutline}`} onClick={() => selecionarTodosAtributos(true)}>Selecionar Todos</button>
                                        <button type="button" className={`${styles.btn} ${styles.btnOutline}`} onClick={() => selecionarTodosAtributos(false)}>Limpar Seleção</button>
                                    </div>
                                    <div className={styles.reportAttributesGrid}>
                                        {Object.keys(reportFilters.atributos).map((key) => (
                                            <div className={styles.formCheck} key={key}>
                                                <input type="checkbox" id={`attr-${key}`} name={key} checked={reportFilters.atributos[key]} onChange={handleReportAtributosChange} />
                                                <label htmlFor={`attr-${key}`}>{getFriendlyName(key)}</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className={styles.modalFooter}>
                                    <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setShowReportModal(false)}>Cancelar</button>
                                    <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={handleGerarRelatorioImoveis}><FaFilePdf /> Gerar PDF</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {showDesramaModal && (
                        <div className={styles.modalOverlay} onClick={() => setShowDesramaModal(false)}>
                            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                                <div className={styles.modalHeader}>
                                    <h3>Previsão de Desrama</h3>
                                    <button className={styles.modalCloseBtn} onClick={() => setShowDesramaModal(false)}><FaTimes /></button>
                                </div>
                                <div className={styles.modalBody}>
                                    <div className={styles.formGroup}>
                                        <label htmlFor="anoPrevisaoDesrama">Selecione o ano da previsão:</label>
                                        <input
                                            type="number"
                                            id="anoPrevisaoDesrama"
                                            name="anoPrevisao"
                                            value={anoPrevisao}
                                            onChange={(e) => setAnoPrevisao(e.target.value)}
                                            placeholder="Ex: 2026"
                                        />
                                    </div>
                                </div>
                                <div className={styles.modalFooter}>
                                    <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setShowDesramaModal(false)}>Cancelar</button>
                                    <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => handleGerarRelatorioPrevisao('desrama')}><FaFilePdf /> Gerar Relatório</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {showDesbasteModal && (
                        <div className={styles.modalOverlay} onClick={() => setShowDesbasteModal(false)}>
                            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                                <div className={styles.modalHeader}>
                                    <h3>Previsão de Desbaste</h3>
                                    <button className={styles.modalCloseBtn} onClick={() => setShowDesbasteModal(false)}><FaTimes /></button>
                                </div>
                                <div className={styles.modalBody}>
                                    <div className={styles.formGroup}>
                                        <label htmlFor="anoPrevisaoDesbaste">Selecione o ano da previsão:</label>
                                        <input
                                            type="number"
                                            id="anoPrevisaoDesbaste"
                                            name="anoPrevisao"
                                            value={anoPrevisao}
                                            onChange={(e) => setAnoPrevisao(e.target.value)}
                                            placeholder="Ex: 2026"
                                        />
                                    </div>
                                </div>
                                <div className={styles.modalFooter}>
                                    <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setShowDesbasteModal(false)}>Cancelar</button>
                                    <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => handleGerarRelatorioPrevisao('desbaste')}><FaFilePdf /> Gerar Relatório</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {showVencimentoModal && (
                        <div className={styles.modalOverlay} onClick={() => setShowVencimentoModal(false)}>
                            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                                <div className={styles.modalHeader}>
                                    <h3>Relatório de Vencimentos</h3>
                                    <button className={styles.modalCloseBtn} onClick={() => setShowVencimentoModal(false)}><FaTimes /></button>
                                </div>
                                <div className={styles.modalBody}>
                                    <div className={styles.formGroup}>
                                        <label htmlFor="anoVencimento">Selecione o ano do vencimento:</label>
                                        <input
                                            type="number"
                                            id="anoVencimento"
                                            name="anoVencimento"
                                            value={anoPrevisao}
                                            onChange={(e) => setAnoPrevisao(e.target.value)}
                                            placeholder="Ex: 2025"
                                        />
                                    </div>
                                </div>
                                <div className={styles.modalFooter}>
                                    <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setShowVencimentoModal(false)}>Cancelar</button>
                                    <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={handleGerarRelatorioVencimentos}><FaFilePdf /> Gerar Relatório</button>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className={styles.controlsBar}>
                        <div className={styles.searchBar}>
                            <FaSearch className={styles.searchIcon} />
                            <input type="text" placeholder="Pesquisar por descrição..." value={searchTerm} onChange={handleSearchChange} />
                        </div>
                        <div className={styles.actions}>
                            <select className={styles.sortSelect} value={tipoImovelFiltro} onChange={(e) => setTipoImovelFiltro(e.target.value)}>
                                <option value="todos">Todos os Imóveis</option>
                                <option value="proprio">Próprios</option>
                                <option value="arrendado">Arrendados</option>
                            </select>

                            <select className={styles.sortSelect} value={sortCriteria} onChange={(e) => setSortCriteria(e.target.value)}>
                                <option value="">Ordenar por...</option>
                                <option value="codigo_cc">Código CC</option>
                                <option value="descricao">Descrição (A-Z)</option>
                                <option value="area_plantio">Área de Plantio (maior)</option>
                                <option value="proprietario">Proprietário (A-Z)</option>
                                <option value="data_plantio_desc">Data de Plantio (recente)</option>
                            </select>
                            <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setShowFilterModal(true)}>
                                <FaFilter /> Filtrar
                            </button>
                            
                            <div className={styles.reportDropdownContainer} ref={reportDropdownRef}>
                                <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => setShowReportDropdown(!showReportDropdown)}>
                                    <FaFileAlt /> Gerar Relatório
                                </button>
                                {showReportDropdown && (
                                    <div className={styles.reportDropdownMenu}>
                                        <button className={styles.reportDropdownItem} onClick={handleImoveisReportClick}>
                                            <FaFilePdf /> Relatório de Imóveis
                                        </button>
                                        <button className={styles.reportDropdownItem} onClick={handleVencimentoReportClick}>
                                            <FaClock /> Relatório de Vencimentos
                                        </button>
                                        <button className={styles.reportDropdownItem} onClick={handleDesramaReportClick}>
                                            <FaCalendarAlt /> Previsão de Desrama
                                        </button>
                                        <button className={styles.reportDropdownItem} onClick={handleDesbasteReportClick}>
                                            <FaCalendarAlt /> Previsão de Desbaste
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className={styles.imoveisList}>
                        {currentItems.length > 0 ? (
                            <table className={styles.imoveisTable}>
                                <thead>
                                    <tr>
                                        <th><FaHashtag className={styles.headerIcon} /> Código CC</th>
                                        <th><FaSignature className={styles.headerIcon} /> Descrição</th>
                                        <th><FaCalendarAlt className={styles.headerIcon} /> Data de Plantio</th>
                                        <th><FaRulerCombined className={styles.headerIcon} /> Área de Plantio (ha)</th>
                                        <th><FaTree className={styles.headerIcon} /> Espécie</th>
                                        <th><FaUserTie className={styles.headerIcon} /> Proprietário</th>
                                        <th className={styles.actionsColumn}><FaEllipsisV className={styles.headerIcon} /> Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentItems.map((imovel) => (
                                        <tr key={imovel.id} onClick={() => handleRowClick(imovel.id)} style={{ cursor: 'pointer' }}> 
                                            <td data-label="Código CC"><span>{imovel.codigo_cc || 'N/A'}</span></td>
                                            <td data-label="Descrição" className={styles.longTextCell} title={imovel.descricao}><span>{imovel.descricao}</span></td>
                                            <td data-label="Data Plantio"><span>{formatarData(imovel.data_plantio)}</span></td>
                                            <td data-label="Área Plantio"><span>{formatarNumero(imovel.area_plantio)}</span></td>
                                            <td data-label="Espécie"><span>{imovel.especie}</span></td>
                                            <td data-label="Proprietário" className={styles.longTextCell} title={imovel.proprietario}><span>{imovel.proprietario}</span></td>
                                            <td className={styles.actionsCell} onClick={(e) => e.stopPropagation()}>
                                                <button className={`${styles.btn} ${styles.btnDanger}`} onClick={() => confirmDelete(imovel.id)}>
                                                    <FaTrashAlt /> <span className={styles.deleteText}>Excluir</span>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className={styles.noResults}>
                                <p>Nenhum imóvel encontrado.</p>
                                <span>Tente ajustar seus filtros ou termo de pesquisa.</span>
                            </div>
                        )}
                    </div>

                    {totalPages > 1 && (
                        <div className={styles.paginationContainer}>
                            <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} className={styles.paginationArrow}>
                                <FaChevronLeft />
                            </button>
                            <span className={styles.paginationInfo}>
                                Página <strong>{currentPage}</strong> de <strong>{totalPages}</strong>
                            </span>
                            <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} className={styles.paginationArrow}>
                                <FaChevronRight />
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ViewImoveis;